/** Pure calculator engine — token-based expression, sequential (non-algebraic) evaluation. */

const MAX_ENTRY_CHARS = 16;
const PRECISION = 14;

export function parseDisplay(str) {
  if (str == null || str === '' || str === 'Error') return NaN;
  const n = Number.parseFloat(str);
  return Number.isFinite(n) ? n : NaN;
}

export function sanitizeNumber(n) {
  if (!Number.isFinite(n)) return n;
  return Number.parseFloat(n.toPrecision(PRECISION));
}

export function opSymbol(op) {
  switch (op) {
    case '/':
      return '÷';
    case '*':
      return '×';
    case '-':
      return '−';
    case '+':
      return '+';
    default:
      return '';
  }
}

export function applyOp(a, b, op) {
  switch (op) {
    case '+':
      return sanitizeNumber(a + b);
    case '-':
      return sanitizeNumber(a - b);
    case '*':
      return sanitizeNumber(a * b);
    case '/':
      if (b === 0) return null;
      return sanitizeNumber(a / b);
    default:
      return null;
  }
}

export function formatDisplay(n) {
  if (n === null) return 'Error';
  if (!Number.isFinite(n)) return 'Error';

  const abs = Math.abs(n);
  if (abs > 0 && (abs >= 1e15 || (abs < 1e-9 && abs > 0))) {
    let exp = n.toExponential(8);
    exp = exp.replace(/\.?0+e/, 'e');
    if (exp.length > MAX_ENTRY_CHARS) exp = n.toExponential(5);
    return exp;
  }

  let s = String(sanitizeNumber(n));
  if (s === '-0') s = '0';
  if (s.includes('e')) return s;
  if (s.length > MAX_ENTRY_CHARS) {
    return n.toExponential(Math.min(8, MAX_ENTRY_CHARS - 6));
  }
  return s;
}

/** @typedef {{ type: 'num', raw: string }} NumToken */
/** @typedef {{ type: 'op', op: '+'|'-'|'*'|'/' }} OpToken */

function tokenToString(t) {
  if (t.type === 'num') return t.raw;
  return opSymbol(t.op);
}

/** Join tokens with spaces for the expression preview. */
export function formatTokens(tokens) {
  return tokens.map(tokenToString).join(' ');
}

/**
 * Fold num (op num)* into a numeric value. Returns { value } or { error: 'div0', ... }.
 */
function foldSequential(tokens) {
  if (!tokens.length) return { value: 0 };
  if (tokens[0].type !== 'num') return { error: 'bad' };
  let acc = parseDisplay(tokens[0].raw);
  if (Number.isNaN(acc)) acc = 0;
  let i = 1;
  while (i < tokens.length) {
    if (tokens[i].type !== 'op') return { error: 'bad' };
    const op = tokens[i].op;
    i += 1;
    if (i >= tokens.length) return { error: 'bad' };
    if (tokens[i].type !== 'num') return { error: 'bad' };
    const b = parseDisplay(tokens[i].raw);
    if (Number.isNaN(b)) return { error: 'bad' };
    const res = applyOp(acc, b, op);
    if (res === null) return { error: 'div0', acc, op, b };
    acc = res;
    i += 1;
  }
  return { value: acc };
}

/**
 * Derive accumulator, pendingOperator, currentValue, replaceEntry, entryDirty from token list.
 * Invariant: tokens alternate num / op / num / … and may end with num or op.
 */
function recomputeDerivedState(tokens) {
  if (!tokens.length) {
    return {
      accumulator: null,
      pendingOperator: null,
      currentValue: '0',
      replaceEntry: false,
      entryDirty: false,
    };
  }

  const last = tokens[tokens.length - 1];

  if (last.type === 'op') {
    const prefix = tokens.slice(0, -1);
    if (!prefix.length) {
      return {
        accumulator: null,
        pendingOperator: null,
        currentValue: '0',
        replaceEntry: false,
        entryDirty: false,
      };
    }
    const folded = foldSequential(prefix);
    if (folded.error === 'div0') {
      const { acc, op, b } = folded;
      const errExpr = `${formatDisplay(acc)} ${opSymbol(op)} ${formatDisplay(b)}`;
      return {
        accumulator: null,
        pendingOperator: null,
        currentValue: '0',
        replaceEntry: false,
        entryDirty: false,
        __divError: errExpr.trim(),
      };
    }
    if (folded.error) {
      return {
        accumulator: null,
        pendingOperator: null,
        currentValue: '0',
        replaceEntry: false,
        entryDirty: false,
      };
    }
    return {
      accumulator: sanitizeNumber(folded.value),
      pendingOperator: last.op,
      currentValue: formatDisplay(folded.value),
      replaceEntry: true,
      entryDirty: false,
    };
  }

  if (last.type !== 'num') {
    return {
      accumulator: null,
      pendingOperator: null,
      currentValue: '0',
      replaceEntry: false,
      entryDirty: false,
    };
  }

  if (tokens.length === 1) {
    return {
      accumulator: null,
      pendingOperator: null,
      currentValue: last.raw,
      replaceEntry: false,
      entryDirty: true,
    };
  }

  const beforeLastNum = tokens.slice(0, -1);
  const opTok = beforeLastNum[beforeLastNum.length - 1];
  if (!opTok || opTok.type !== 'op') {
    return {
      accumulator: null,
      pendingOperator: null,
      currentValue: last.raw,
      replaceEntry: false,
      entryDirty: true,
    };
  }
  const prefix = beforeLastNum.slice(0, -1);
  const folded = foldSequential(prefix);
  if (folded.error === 'div0') {
    const { acc, op, b } = folded;
    const errExpr = `${formatDisplay(acc)} ${opSymbol(op)} ${formatDisplay(b)}`;
    return {
      accumulator: null,
      pendingOperator: null,
      currentValue: last.raw,
      replaceEntry: false,
      entryDirty: true,
      __divError: errExpr.trim(),
    };
  }
  if (folded.error) {
    return {
      accumulator: null,
      pendingOperator: null,
      currentValue: last.raw,
      replaceEntry: false,
      entryDirty: true,
    };
  }
  return {
    accumulator: sanitizeNumber(folded.value),
    pendingOperator: opTok.op,
    currentValue: last.raw,
    replaceEntry: false,
    entryDirty: true,
  };
}

function mergeDerived(base, derived) {
  const { __divError, ...rest } = derived;
  if (__divError) {
    return {
      ...initialCalculatorState,
      errorState: true,
      errorExpression: __divError,
    };
  }
  return { ...base, ...rest };
}

/**
 * Top expression line: frozen after =, error context, or live from tokens.
 */
export function getExpressionLine(state) {
  if (state.errorState) {
    return state.errorExpression ?? '';
  }
  if (state.frozenExpression) {
    return state.frozenExpression;
  }
  return formatTokens(state.expressionTokens);
}

/** Bottom display string */
export function getMainDisplay(state) {
  if (state.errorState) return 'Error';
  return state.currentValue;
}

export const initialCalculatorState = {
  expressionTokens: [],
  currentValue: '0',
  accumulator: null,
  pendingOperator: null,
  replaceEntry: false,
  entryDirty: false,
  lastOperator: null,
  lastOperand: null,
  justEvaluated: false,
  frozenExpression: null,
  errorExpression: null,
  errorState: false,
};

function clearRepeat(partial) {
  return { ...partial, lastOperator: null, lastOperand: null };
}

function clearFrozen(partial) {
  return {
    ...partial,
    frozenExpression: null,
    errorExpression: null,
    errorState: false,
  };
}

function replaceLastOpToken(tokens, newOp) {
  if (tokens.length === 0) return [{ type: 'op', op: newOp }];
  const last = tokens[tokens.length - 1];
  if (last.type === 'op') {
    return [...tokens.slice(0, -1), { type: 'op', op: newOp }];
  }
  return [...tokens, { type: 'op', op: newOp }];
}

function appendNum(tokens, raw) {
  return [...tokens, { type: 'num', raw: String(raw) }];
}

function appendOp(tokens, op) {
  return [...tokens, { type: 'op', op }];
}

function updateLastNumRaw(tokens, raw) {
  if (!tokens.length) return appendNum([], raw);
  const last = tokens[tokens.length - 1];
  if (last.type !== 'num') return [...tokens, { type: 'num', raw: String(raw) }];
  return [...tokens.slice(0, -1), { type: 'num', raw: String(raw) }];
}

function operandStrForError(cur, rawEntry) {
  if (rawEntry !== '' && !Number.isNaN(cur)) return rawEntry;
  return formatDisplay(cur);
}

export function calculatorReducer(state, action) {
  switch (action.type) {
    case 'CLEAR':
      return { ...initialCalculatorState };

    case 'DIGIT': {
      const d = action.digit;
      if (d === undefined || d === '') return state;
      if (state.errorState) {
        return calculatorReducer(initialCalculatorState, action);
      }

      if (state.justEvaluated) {
        const tokens = appendNum([], d === '0' ? '0' : d);
        const derived = recomputeDerivedState(tokens);
        return clearRepeat(
          mergeDerived(
            clearFrozen({
              ...initialCalculatorState,
              expressionTokens: tokens,
            }),
            derived,
          ),
        );
      }

      let tokens = [...state.expressionTokens];

      if (state.replaceEntry) {
        const last = tokens[tokens.length - 1];
        if (last && last.type === 'op') {
          tokens = appendNum(tokens, d === '0' ? '0' : d);
        } else {
          tokens = updateLastNumRaw(tokens, d === '0' ? '0' : d);
        }
        const derived = recomputeDerivedState(tokens);
        return clearRepeat(mergeDerived({ ...clearFrozen(state), expressionTokens: tokens }, derived));
      }

      const lastTok = tokens[tokens.length - 1];
      if (!tokens.length || lastTok.type === 'op') {
        tokens = appendNum(tokens, d === '0' ? '0' : d);
      } else {
        let raw = lastTok.raw;
        if (raw === '0' && d !== '0') raw = d;
        else if (raw === '0' && d === '0') raw = '0';
        else raw = raw + d;
        if (raw.replace(/^-/, '').length > MAX_ENTRY_CHARS) return state;
        tokens = updateLastNumRaw(tokens, raw);
      }
      const derived = recomputeDerivedState(tokens);
      return clearRepeat(mergeDerived({ ...clearFrozen(state), expressionTokens: tokens }, derived));
    }

    case 'DECIMAL': {
      if (state.errorState) {
        return calculatorReducer(initialCalculatorState, { type: 'DECIMAL' });
      }

      if (state.justEvaluated) {
        const tokens = appendNum([], '0.');
        const derived = recomputeDerivedState(tokens);
        return clearRepeat(
          mergeDerived(
            clearFrozen({
              ...initialCalculatorState,
              expressionTokens: tokens,
            }),
            derived,
          ),
        );
      }

      let tokens = [...state.expressionTokens];

      if (state.replaceEntry) {
        const last = tokens[tokens.length - 1];
        if (last && last.type === 'op') {
          tokens = appendNum(tokens, '0.');
        } else {
          tokens = updateLastNumRaw(tokens, '0.');
        }
        const derived = recomputeDerivedState(tokens);
        return clearRepeat(mergeDerived({ ...clearFrozen(state), expressionTokens: tokens }, derived));
      }

      const lastTok = tokens[tokens.length - 1];
      if (!tokens.length || lastTok.type === 'op') {
        tokens = appendNum(tokens, '0.');
      } else {
        let raw = lastTok.raw;
        if (raw.includes('.')) return state;
        raw = raw === '' ? '0.' : `${raw}.`;
        tokens = updateLastNumRaw(tokens, raw);
      }
      const derived = recomputeDerivedState(tokens);
      return clearRepeat(mergeDerived({ ...clearFrozen(state), expressionTokens: tokens }, derived));
    }

    case 'SIGN': {
      if (state.errorState) {
        return calculatorReducer(initialCalculatorState, action);
      }
      let base = state;
      if (state.justEvaluated) {
        const t = appendNum([], state.currentValue);
        const d0 = recomputeDerivedState(t);
        const merged = mergeDerived(
          {
            ...state,
            expressionTokens: t,
            justEvaluated: false,
            frozenExpression: null,
            lastOperator: null,
            lastOperand: null,
          },
          d0,
        );
        if (merged.errorState) return merged;
        base = merged;
      }

      let tokens = [...base.expressionTokens];
      const last = tokens[tokens.length - 1];
      if (last && last.type === 'op' && base.replaceEntry && !base.entryDirty && base.accumulator != null) {
        const acc = sanitizeNumber(-base.accumulator);
        return clearRepeat({
          ...clearFrozen(base),
          accumulator: acc,
          currentValue: formatDisplay(acc),
          replaceEntry: true,
          entryDirty: false,
        });
      }
      if (!last || last.type !== 'num') return base;

      let raw = last.raw;
      if (raw === '0' || raw === '0.') return base;
      if (raw.startsWith('-')) raw = raw.slice(1);
      else raw = `-${raw}`;
      tokens = updateLastNumRaw(tokens, raw);
      const derived = recomputeDerivedState(tokens);
      return clearRepeat(mergeDerived({ ...clearFrozen(base), expressionTokens: tokens }, derived));
    }

    case 'PERCENT': {
      if (state.errorState) {
        return calculatorReducer(initialCalculatorState, action);
      }
      let s = state;
      if (state.justEvaluated) {
        const t = appendNum([], state.currentValue);
        const d0 = recomputeDerivedState(t);
        const merged = mergeDerived(
          {
            ...state,
            expressionTokens: t,
            justEvaluated: false,
            frozenExpression: null,
            lastOperator: null,
            lastOperand: null,
          },
          d0,
        );
        if (merged.errorState) return merged;
        s = merged;
      }

      const { accumulator, pendingOperator } = s;
      let tokens = [...s.expressionTokens];
      const last = tokens[tokens.length - 1];
      if (!last || last.type !== 'num') return state;

      let n = parseDisplay(last.raw);
      if (Number.isNaN(n)) return s;

      if (pendingOperator === '+' || pendingOperator === '-') {
        if (accumulator === null) return state;
        n = sanitizeNumber(accumulator * (n / 100));
      } else if (pendingOperator === '*' || pendingOperator === '/') {
        n = sanitizeNumber(n / 100);
      } else {
        n = sanitizeNumber(n / 100);
      }

      const newRaw = formatDisplay(n);
      tokens = updateLastNumRaw(tokens, newRaw);
      const derived = recomputeDerivedState(tokens);
      return clearRepeat(
        mergeDerived(
          {
            ...clearFrozen(s),
            expressionTokens: tokens,
          },
          { ...derived, replaceEntry: true, entryDirty: false },
        ),
      );
    }

    case 'BACKSPACE': {
      if (state.errorState) {
        return { ...initialCalculatorState };
      }

      if (state.justEvaluated) {
        const cv = state.currentValue;
        if (cv.length <= 1) {
          return { ...initialCalculatorState };
        }
        let nv = cv.slice(0, -1);
        if (nv === '-' || nv === '') nv = '0';
        return clearRepeat({
          ...clearFrozen(state),
          expressionTokens: [],
          accumulator: null,
          pendingOperator: null,
          justEvaluated: false,
          frozenExpression: null,
          currentValue: nv,
          replaceEntry: false,
          entryDirty: true,
        });
      }

      let tokens = [...state.expressionTokens];

      if (tokens.length === 0) {
        const cv = state.currentValue;
        if (cv.length > 1) {
          let nv = cv.slice(0, -1);
          if (nv === '-' || nv === '') nv = '0';
          return clearRepeat({
            ...clearFrozen(state),
            currentValue: nv,
            replaceEntry: false,
            entryDirty: true,
          });
        }
        return { ...initialCalculatorState };
      }

      const last = tokens[tokens.length - 1];

      if (last.type === 'op') {
        tokens = tokens.slice(0, -1);
        const derived = recomputeDerivedState(tokens);
        return clearRepeat(mergeDerived({ ...clearFrozen(state), expressionTokens: tokens }, derived));
      }

      if (last.type === 'num') {
        if (last.raw.length > 1) {
          let raw = last.raw.slice(0, -1);
          if (raw === '-') raw = '0';
          tokens = [...tokens.slice(0, -1), { type: 'num', raw }];
          const derived = recomputeDerivedState(tokens);
          return clearRepeat(mergeDerived({ ...clearFrozen(state), expressionTokens: tokens }, derived));
        }
        tokens = tokens.slice(0, -1);
        const derived = recomputeDerivedState(tokens);
        return clearRepeat(mergeDerived({ ...clearFrozen(state), expressionTokens: tokens }, derived));
      }

      return state;
    }

    case 'OPERATOR': {
      const op = action.op;
      if (state.errorState) {
        return calculatorReducer(initialCalculatorState, action);
      }

      let tokens = [...state.expressionTokens];
      let next = {
        ...clearFrozen(state),
        frozenExpression: null,
        justEvaluated: false,
      };

      if (state.justEvaluated) {
        const cur = parseDisplay(state.currentValue);
        if (Number.isNaN(cur)) return state;
        tokens = appendOp(appendNum([], state.currentValue), op);
        const derived = recomputeDerivedState(tokens);
        return clearRepeat(mergeDerived({ ...next, expressionTokens: tokens }, derived));
      }

      const {
        accumulator,
        pendingOperator,
        replaceEntry,
        entryDirty,
        currentValue,
      } = state;

      const cur = parseDisplay(currentValue);
      if (Number.isNaN(cur)) return state;

      if (accumulator !== null && pendingOperator != null && replaceEntry && !entryDirty) {
        tokens = replaceLastOpToken(tokens, op);
        const derived = recomputeDerivedState(tokens);
        return clearRepeat(mergeDerived({ ...next, expressionTokens: tokens }, derived));
      }

      if (accumulator !== null && pendingOperator != null && !replaceEntry) {
        const lastTok = tokens[tokens.length - 1];
        if (!lastTok || lastTok.type !== 'num') return state;
        const b = parseDisplay(lastTok.raw);
        const res = applyOp(accumulator, b, pendingOperator);
        if (res === null) {
          const errExpr = `${formatDisplay(accumulator)} ${opSymbol(pendingOperator)} ${operandStrForError(b, lastTok.raw)}`;
          return {
            ...initialCalculatorState,
            errorState: true,
            errorExpression: errExpr.trim(),
          };
        }
        tokens = appendOp(tokens, op);
        const derived = recomputeDerivedState(tokens);
        const resStr = formatDisplay(res);
        return clearRepeat(
          mergeDerived(
            {
              ...next,
              expressionTokens: tokens,
            },
            { ...derived, currentValue: resStr, replaceEntry: true, entryDirty: false },
          ),
        );
      }

      const lastTokForFirst = tokens[tokens.length - 1];
      if (lastTokForFirst && lastTokForFirst.type === 'num') {
        tokens = appendOp(tokens, op);
      } else if (!tokens.length) {
        tokens = appendOp(appendNum([], currentValue), op);
      } else {
        tokens = appendOp(appendNum([], currentValue), op);
      }
      const derived = recomputeDerivedState(tokens);
      return clearRepeat(mergeDerived({ ...next, expressionTokens: tokens }, derived));
    }

    case 'EQUALS': {
      if (state.errorState) return state;

      const {
        accumulator,
        pendingOperator,
        currentValue,
        lastOperator,
        lastOperand,
        expressionTokens,
        replaceEntry,
        entryDirty,
        justEvaluated,
      } = state;

      const cur = parseDisplay(currentValue);
      if (Number.isNaN(cur)) return state;

      if (
        lastOperator &&
        lastOperand != null &&
        (accumulator === null || pendingOperator === null) &&
        justEvaluated
      ) {
        const res = applyOp(cur, lastOperand, lastOperator);
        if (res === null) {
          const errExpr = `${formatDisplay(cur)} ${opSymbol(lastOperator)} ${formatDisplay(lastOperand)}`;
          return {
            ...initialCalculatorState,
            errorState: true,
            errorExpression: errExpr,
          };
        }
        return {
          ...state,
          currentValue: formatDisplay(res),
          replaceEntry: true,
          entryDirty: false,
          errorState: false,
        };
      }

      if (accumulator === null || pendingOperator === null) return state;

      const lastTok = expressionTokens[expressionTokens.length - 1];
      const operand = replaceEntry && !entryDirty ? accumulator : cur;
      const operandRaw =
        replaceEntry && !entryDirty ? formatDisplay(accumulator) : lastTok?.type === 'num' ? lastTok.raw : currentValue;
      const res = applyOp(accumulator, operand, pendingOperator);
      if (res === null) {
        const errExpr = `${formatDisplay(accumulator)} ${opSymbol(pendingOperator)} ${operandStrForError(operand, operandRaw)}`;
        return {
          ...initialCalculatorState,
          errorState: true,
          errorExpression: errExpr.trim(),
        };
      }

      const endsWithOp = expressionTokens[expressionTokens.length - 1]?.type === 'op';
      const freezeTokens = endsWithOp
        ? appendNum([...expressionTokens], formatDisplay(operand))
        : expressionTokens;
      const fullExpr = formatTokens(freezeTokens);
      const resStr = formatDisplay(res);

      return {
        ...clearFrozen(state),
        expressionTokens: [],
        accumulator: null,
        pendingOperator: null,
        currentValue: resStr,
        replaceEntry: true,
        entryDirty: false,
        frozenExpression: fullExpr,
        justEvaluated: true,
        lastOperator: pendingOperator,
        lastOperand: operand,
      };
    }

    default:
      return state;
  }
}
